import React from 'react';
import { Box, ContentTemplate, KeyValue, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { GetGroupSCReviewResponse } from '../../../api-refactored/group-special-consideration/useGetGroupSCReview';
import { stringToSemanticDisplay } from '../../../utils/DateUtils';
import DocumentBadge from '../../../components/DocumentBadge';
import RankingDetailsTable from './RankingDetailsTable';
import { UploadedFileModel } from '../../../api-refactored/special-consideration/useGetUploadedFileInfo';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  marginBottom4: {
    marginBottom: theme.spacing(4),
  },
  fileContainer: {
    marginBottom: theme.spacing(8),
  },
  submittedDocuments: {
    marginBottom: theme.spacing(1),
  },
  keyLabel: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(2),
  },
}));

interface ApplicationDetailsContentProps {
  groupSCReview: GetGroupSCReviewResponse;
  isSummary?: boolean;
  amendment?: boolean;
}

const DetailsContent = ({
  groupSCReview,
  isSummary = false,
  amendment = false,
}: ApplicationDetailsContentProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const AddedDocumentsDisplay = () =>
    groupSCReview.fileNames
      .filter((file: UploadedFileModel) => !file.isSubmitted)
      .map((file: UploadedFileModel) => (
        <DocumentBadge
          className={classes.marginBottom4}
          documentName={file.fileName}
          key={file.fileUuid}
        />
      ));

  const SubmittedDocumentsDisplay = () =>
    groupSCReview?.fileNames
      .filter((file: UploadedFileModel) => file.isSubmitted)
      .map((file: UploadedFileModel) => (
        <DocumentBadge
          className={classes.marginBottom4}
          documentName={file.fileName}
          key={file.fileUuid}
        />
      ));

  const DocumentDisplayNonAmendment = () =>
    groupSCReview.fileNames
      .filter((file: UploadedFileModel) => !isSummary || file.isSubmitted)
      .map((file: UploadedFileModel) => (
        <DocumentBadge
          className={classes.marginBottom4}
          documentName={file.fileName}
          key={file.fileUuid}
        />
      ));

  return (
    <>
      <ContentTemplate isDividerTop={isSummary} type="large" continuous={!isSummary} isContinuable>
        <Typography variant="h3" className={classes.marginBottom4}>
          {t('specialConsideration->>ApplyForSpecialConsideration.SubjectPaperDetails')}
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)">
          <KeyValue
            id="examLevel"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.ExamLevel')}
            valueText={groupSCReview.subjectDetail.examLevel}
          />
          <KeyValue
            id="examSeries"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.Exam Series')}
            valueText={groupSCReview.subjectDetail.examSeries}
          />
        </Box>
        <Box>
          <KeyValue
            id="modeOfAssessment"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.ModeOfAssessment')}
            valueText={groupSCReview.subjectDetail.modeOfAssessment}
          />
        </Box>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)">
          <KeyValue
            id="examDate"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.ExamDate')}
            valueText={stringToSemanticDisplay(groupSCReview.subjectDetail.examDate)}
          />
          <KeyValue
            id="examCentre"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.ExamCentre')}
            valueText={groupSCReview.examCentre}
          />
        </Box>
        <Box>
          <KeyValue
            id="subject"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.SubjectPaper')}
            valueText={groupSCReview.subjectDetail.subject}
          />
        </Box>
      </ContentTemplate>
      <RankingDetailsTable
        groupScType={groupSCReview.groupScType}
        examLevel={groupSCReview.subjectDetail.examLevel}
        modeOfAssessment={groupSCReview.subjectDetail.modeOfAssessment}
      />
      <ContentTemplate isDividerTop type="large" isContinuable>
        <Typography variant="h3" className={classes.marginBottom4}>
          {t('specialConsideration->>ApplyForSpecialConsideration.SpecialConsiderationDetails')}
        </Typography>
        <Box>
          <KeyValue
            id="specialConsiderationType"
            keyText={t(
              'specialConsideration->>ApplyForSpecialConsideration.Special Consideration Type'
            )}
            valueText={groupSCReview.specialConsiderationDetail.specialConsiderationType}
          />
        </Box>
        <Box>
          <KeyValue
            id="reason"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.Reason')}
            valueText={groupSCReview.specialConsiderationDetail.reason}
          />
        </Box>
        <Box>
          <KeyValue
            id="explanation"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.Remarks')}
            valueText={groupSCReview.specialConsiderationDetail.explanation}
          />
        </Box>
      </ContentTemplate>
      <ContentTemplate isDividerTop type="large" isContinuable>
        <Typography variant="h3" className={classes.marginBottom4}>
          {t('specialConsideration->>ApplyForSpecialConsideration.SupportingDocuments')}
        </Typography>
        <Box className={classes.fileContainer}>
          {amendment ? (
            <>
              <Box className={classes.submittedDocuments}>
                <Typography variant="h5" className={classes.keyLabel}>
                  {t('specialConsideration->>ApplyForSpecialConsideration.AddedDocuments')}
                </Typography>
                {AddedDocumentsDisplay()}
              </Box>
              <Typography variant="h5" className={classes.keyLabel}>
                {t('specialConsideration->>ApplyForSpecialConsideration.SubmittedDocuments')}
              </Typography>
              {SubmittedDocumentsDisplay()}
            </>
          ) : (
            <>{DocumentDisplayNonAmendment()}</>
          )}
        </Box>
      </ContentTemplate>
    </>
  );
};

export default DetailsContent;
